import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import { Send, Flag, Clock, AlertTriangle, Loader2, Radio } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROOM_LABELS = {
  GENERAL: 'General',
  FARRIER: 'Farrier',
  LIVESTOCK: 'Livestock',
  AFTER_HOURS: 'After Hours',
};

export default function LiveChat() {
  const { token } = useAuth();
  const [room, setRoom] = useState('GENERAL');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    loadMessages();
    // Poll every 5 seconds
    pollRef.current = setInterval(loadMessages, 5000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  const loadMessages = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/live-chat/messages/${room}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(res.data || []);
    } catch (e) {
      console.error('Error loading chat messages', e);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    const body = newMessage.trim();
    if (!body) return;
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/live-chat/messages`,
        { room, body },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      await loadMessages();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send message';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const reportMessage = async (messageId) => {
    if (!window.confirm('Report this message to admins?')) return;
    try {
      await axios.post(
        `${API_URL}/api/live-chat/report/${messageId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Message reported');
      await loadMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to report');
    }
  };

  const formatTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <div className="space-y-4" data-testid="live-chat">
      <Alert className="bg-blue-50 border-blue-200">
        <Clock className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Ephemeral chat:</strong> All messages auto-delete after 24 hours. Rate limit: 10
          messages per minute. Be respectful — abusive messages may result in a mute.
        </AlertDescription>
      </Alert>

      <Tabs value={room} onValueChange={setRoom}>
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          {Object.entries(ROOM_LABELS).map(([value, label]) => (
            <TabsTrigger key={value} value={value} data-testid={`chat-room-${value.toLowerCase()}`}>
              <Radio className="h-3 w-3 mr-1 hidden sm:inline" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card className="h-[500px] flex flex-col" data-testid="chat-messages-card">
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Radio className="h-12 w-12 opacity-40 mb-2" />
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages
              .filter((m) => m.status !== 'HIDDEN' && m.status !== 'DELETED')
              .map((msg) => (
                <div key={msg.id} className="group flex flex-col" data-testid={`chat-msg-${msg.id}`}>
                  <div className="flex items-baseline gap-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{msg.providerName || 'Provider'}</span>
                    {msg.businessName && <span>• {msg.businessName}</span>}
                    <span>• {formatTime(msg.createdAt)}</span>
                  </div>
                  <div className="flex items-start justify-between gap-2 mt-0.5">
                    <p className="whitespace-pre-wrap text-sm flex-1">{msg.body}</p>
                    <button
                      onClick={() => reportMessage(msg.id)}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-600 transition-opacity"
                      title="Report"
                      data-testid={`report-msg-${msg.id}`}
                    >
                      <Flag className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {msg.reportCount > 0 && (
                    <Badge variant="outline" className="text-xs w-fit mt-1 text-yellow-700 border-yellow-300">
                      <AlertTriangle className="h-3 w-3 mr-1" /> {msg.reportCount} report{msg.reportCount > 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
              ))
          )}
          <div ref={messagesEndRef} />
        </CardContent>

        <div className="border-t p-3 flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder={`Message #${ROOM_LABELS[room]}...`}
            maxLength={1000}
            disabled={sending}
            data-testid="chat-input"
          />
          <Button
            onClick={sendMessage}
            disabled={sending || !newMessage.trim()}
            data-testid="chat-send-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </Card>
    </div>
  );
}
